import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import axios from "axios";
import { AuthContext } from "@/App";
import { useTableDensity } from "@/hooks/useTableDensity";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import PageTitle from "@/components/PageTitle";
import {
  Plus, Search, Pencil, Trash2, Download, Loader2, X, Recycle,
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENT = "#7c3aed"; // Sales area accent (violet — distinct from Stores orange / SC emerald)

// ── Formatting helpers ──
const fmtINR = (n) => "₹" + Math.round(n || 0).toLocaleString("en-IN");
const fmtUSD = (n) => "$" + (n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtKg = (n) => (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
// USD/kg scrap rates are small — show 3 decimals so $0.371 ≠ $0.37.
const fmtRate = (n) => "$" + (n || 0).toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};
const monthKey = (iso) => (iso || "").slice(0, 7);
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};

// Indian financial year (Apr–Mar) bounds containing `today`.
function fyBounds(today = new Date()) {
  const start = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return {
    from: `${start}-04-01`,
    to: `${start + 1}-03-31`,
    label: `FY ${String(start).slice(2)}–${String(start + 1).slice(2)}`,
  };
}

// USD-native money for the live preview: value = weight × rate (USD), plus a ₹
// reference at the current fx (USD per ₹).
function computeSale(qty, rateUsd, fx) {
  const usd = qty * rateUsd;
  return { usd, inr: fx ? usd / fx : 0 };
}

function exportScrapCSV(sales) {
  const q = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const cols = [
    ["Invoice no", (s) => s.invoice_no],
    ["Invoice date", (s) => s.invoice_date],
    ["Customer", (s) => s.customer],
    ["Material", (s) => s.material],
    ["Qty (kg)", (s) => s.qty_kg],
    ["Rate (USD)", (s) => s.rate_usd],
    ["Total Value", (s) => s.value_usd],
    ["Value (INR ref)", (s) => s.value_inr],
    ["Vehicle no", (s) => s.vehicle_no],
    ["Payment term", (s) => s.payment_term],
    ["Notes", (s) => s.notes],
  ];
  const lines = [cols.map((c) => q(c[0])).join(",")];
  sales.forEach((s) => lines.push(cols.map((c) => q(c[1](s))).join(",")));
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `scrap-sales-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Add / edit modal — enter weight + USD rate, value computed live ──
function ScrapSaleModal({ sale, meta, onClose, onDone }) {
  const isEdit = !!sale;
  const [customer, setCustomer] = useState(sale?.customer || "");
  const [material, setMaterial] = useState(sale?.material || "");
  const [date, setDate] = useState(sale?.invoice_date || new Date().toISOString().slice(0, 10));
  const [qty, setQty] = useState(sale ? String(sale.qty_kg) : "");
  const [rate, setRate] = useState(sale ? String(sale.rate_usd) : "");
  const [vehicle, setVehicle] = useState(sale?.vehicle_no || "");
  const [invoiceNo, setInvoiceNo] = useState(sale?.invoice_no || meta?.next_invoice_no || "");
  const [notes, setNotes] = useState(sale?.notes || "");
  const [saving, setSaving] = useState(false);

  const fx = sale?.fx_inr_usd || meta?.fx_inr_usd || 0;
  const nQty = parseFloat(qty) || 0;
  const nRate = parseFloat(rate) || 0;
  const calc = computeSale(nQty, nRate, fx);
  const ready = customer.trim() && material.trim() && nQty > 0 && nRate > 0 && date;

  const save = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const body = {
        customer: customer.trim(), material: material.trim(), invoice_date: date,
        qty_kg: nQty, rate_usd: nRate, vehicle_no: vehicle.trim(),
        invoice_no: invoiceNo.trim(), notes: notes.trim(),
        payment_term: sale?.payment_term || meta?.defaults?.payment_term || "Advance 100%",
      };
      if (isEdit) await axios.put(`${API}/sales/scrap/${sale.id}`, body);
      else await axios.post(`${API}/sales/scrap`, body);
      toast.success(isEdit ? "Sale updated" : `Sale recorded — ${fmtUSD(calc.usd)}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save sale");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full h-9 px-3 rounded-md bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))] text-sm text-[hsl(var(--foreground))] focus:outline-none focus:border-violet-500/60 focus:bg-[hsl(var(--card))]";
  const labelCls = "block text-[11px] font-medium text-[hsl(var(--muted-foreground))] mb-1";
  const onEnter = (e) => { if (e.key === "Enter") save(); };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="scrap-sale-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Recycle className="w-5 h-5" style={{ color: ACCENT }} />
            {isEdit ? "Edit scrap sale" : "Record scrap sale"}
          </DialogTitle>
          <DialogDescription>
            Enter the weight and USD rate — the sale value is worked out for you, with a ₹ reference.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3" onKeyDown={onEnter}>
          <div className="col-span-2">
            <label className={labelCls}>Customer *</label>
            <input className={inputCls} value={customer} onChange={(e) => setCustomer(e.target.value)}
                   list="scrap-customers" placeholder="Raj Enterprises" data-testid="scrap-customer" autoFocus />
            <datalist id="scrap-customers">{(meta?.customers || []).map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <div>
            <label className={labelCls}>Material *</label>
            <input className={inputCls} value={material} onChange={(e) => setMaterial(e.target.value)}
                   list="scrap-materials" placeholder="MS turning scrap" data-testid="scrap-material" />
            <datalist id="scrap-materials">{(meta?.materials || []).map((m) => <option key={m} value={m} />)}</datalist>
          </div>
          <div>
            <label className={labelCls}>Invoice date *</label>
            <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} data-testid="scrap-date" />
          </div>
          <div>
            <label className={labelCls}>Weight (kg) *</label>
            <input type="number" min="0" step="any" inputMode="decimal" className={`${inputCls} font-mono-data`}
                   value={qty} onChange={(e) => setQty(e.target.value)} placeholder="9240" data-testid="scrap-qty" />
          </div>
          <div>
            <label className={labelCls}>Rate ($/kg) *</label>
            <input type="number" min="0" step="any" inputMode="decimal" className={`${inputCls} font-mono-data`}
                   value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0.330" data-testid="scrap-rate" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Vehicle no</label>
            <input className={`${inputCls} font-mono-data`} value={vehicle}
                   onChange={(e) => setVehicle(e.target.value.toUpperCase())} placeholder="PB65BF4663" data-testid="scrap-vehicle" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Invoice no</label>
            <input className={`${inputCls} font-mono-data text-xs`} value={invoiceNo}
                   onChange={(e) => setInvoiceNo(e.target.value)} data-testid="scrap-invoice-no" />
          </div>
        </div>
        <div className="mt-1 rounded-lg bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))]/60 px-3.5 py-2.5 text-[12.5px]" data-testid="scrap-calc-panel">
          <div className="flex justify-between py-0.5">
            <span className="text-[hsl(var(--muted-foreground))]">Weight × rate</span>
            <span className="font-mono-data text-[hsl(var(--muted-foreground))]">{fmtKg(nQty)} kg × {rate ? fmtRate(nRate) : "—"}</span>
          </div>
          <div className="flex justify-between pt-1.5 mt-1 border-t border-[hsl(var(--border))]/60 font-semibold">
            <span>Sale value</span>
            <span className="font-mono-data">
              {fmtUSD(calc.usd)}
              <span className="ml-1.5 font-normal text-[hsl(var(--muted-foreground))]">≈ {fmtINR(calc.inr)}</span>
            </span>
          </div>
        </div>
        <textarea
          className="w-full min-h-[52px] px-3 py-2 rounded-md bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))] text-sm text-[hsl(var(--foreground))] focus:outline-none focus:border-violet-500/60 resize-y"
          value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" data-testid="scrap-notes"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose}
                  className="px-4 py-2 rounded-md text-sm border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/50 transition-colors">
            Cancel
          </button>
          <button onClick={save} disabled={!ready || saving} data-testid="scrap-save"
                  title={ready ? "" : "Fill customer, material, date, weight and rate first"}
                  className={`px-4 py-2 rounded-md text-sm font-semibold text-white transition-colors inline-flex items-center gap-1.5 ${ready && !saving ? "bg-violet-600 hover:bg-violet-500 rl-lit-btn" : "bg-violet-600/40 cursor-not-allowed"}`}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Save changes" : "Save sale"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── CSV import helpers ──────────────────────────────────────────────────────
// Canonical template = this page's own export headers, so a round-trip
// (export → re-import in upsert mode) is a no-op.
const TEMPLATE_HEADERS = [
  "Invoice no", "Invoice date", "Customer", "Material", "Qty (kg)", "Rate (USD)",
  "Total Value", "Vehicle no", "Payment term", "Notes",
];

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[₹$,%\s]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Minimal, quote-aware CSV parser with delimiter auto-detect (Excel locales).
function parseCSVText(text) {
  const raw = String(text || "").replace(/^﻿/, "").replace(/\r\n?/g, "\n");
  const lines = raw.split("\n").filter((l) => l.trim().length);
  if (!lines.length) return { headers: [], rows: [] };
  const head = lines[0];
  const count = (ch) => head.split(ch).length - 1;
  const delim = count(";") > count(",") ? ";" : count("\t") > count(",") ? "\t" : ",";
  const parseLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) { if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else inQ = false; } else cur += c; }
      else { if (c === '"') inQ = true; else if (c === delim) { out.push(cur); cur = ""; } else cur += c; }
    }
    out.push(cur); return out.map((s) => s.trim().replace(/^["']|["']$/g, ""));
  };
  return { headers: parseLine(lines[0]), rows: lines.slice(1).map(parseLine) };
}

const _MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
const _pad = (x) => String(x).padStart(2, "0");
// Accept ISO, month-name (04.April.2026), and day-first numeric; else "".
function normalizeDate(raw) {
  const s = String(raw || "").trim().replace(/^["']|["']$/g, "");
  if (!s) return "";
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${m[1]}-${_pad(+m[2])}-${_pad(+m[3])}`;
  const mon = s.toLowerCase().match(/[a-z]{3,9}/);
  if (mon && _MONTHS[mon[0].slice(0, 3)]) {
    const nums = (s.match(/\d+/g) || []).map((x) => parseInt(x, 10));
    const day = nums.find((x) => x >= 1 && x <= 31);
    let year = nums.find((x) => x > 31);
    if (year === undefined) year = nums.find((x) => x !== day);
    if (day && year !== undefined) { if (year < 100) year += 2000; return `${year}-${_pad(_MONTHS[mon[0].slice(0, 3)])}-${_pad(day)}`; }
  }
  m = s.match(/^(\d{1,4})[/.\-](\d{1,2})[/.\-](\d{1,4})$/);
  if (m) {
    const a = +m[1], b = +m[2]; let c = +m[3];
    if (m[1].length === 4) return `${a}-${_pad(b)}-${_pad(c)}`;
    if (c < 100) c += 2000;
    let day = a, month = b;
    if (a > 12) { day = a; month = b; } else if (b > 12) { month = a; day = b; }
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) return `${c}-${_pad(month)}-${_pad(day)}`;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;
}

// Header → canonical key. Normalise to bare alphanumerics so "Qty (kg)",
// "QTY (KGS)" and "qty_kg" all collapse together. USD-native: rate in USD, and
// the sheet's Total Value (USD) is authoritative.
const HEADER_ALIASES = {
  invoiceno: "invoice_no",
  invoicedate: "invoice_date",
  customer: "customer",
  material: "material", description: "material",
  qtykg: "qty_kg", qtykgs: "qty_kg", weightkg: "qty_kg", weight: "qty_kg",
  rateusd: "rate_usd", rate: "rate_usd", rateusdkg: "rate_usd",
  vehicleno: "vehicle_no", vehno: "vehicle_no",
  paymentterm: "payment_term",
  notes: "notes",
  valueusd: "value_usd", totalvalue: "value_usd", totalvalueusd: "value_usd", value: "value_usd",
  valueinrref: "value_inr_ignored",   // our export's ₹-ref column — recomputed, not read
};
const canonKey = (h) => HEADER_ALIASES[String(h || "").toLowerCase().replace(/[^a-z0-9]/g, "")] || null;

// Build a backend row from one CSV line. Total Value (USD) is authoritative; the
// rate is sent through as a hint and the backend derives the precise rate from
// the value when present.
function csvRowToSale(keys, values) {
  const o = {};
  keys.forEach((k, i) => { if (k) o[k] = values[i] ?? ""; });
  return {
    invoice_no: (o.invoice_no || "").trim(),
    invoice_date: normalizeDate(o.invoice_date),
    customer: (o.customer || "").trim(),
    material: (o.material || "").trim(),
    qty_kg: toNum(o.qty_kg),
    rate_usd: toNum(o.rate_usd),
    value_usd: toNum(o.value_usd) || null,
    vehicle_no: (o.vehicle_no || "").trim(),
    payment_term: (o.payment_term || "").trim() || "Advance 100%",
    notes: (o.notes || "").trim(),
  };
}

function downloadTemplate() {
  const example = ["26-27/01SCBH0001", "2026-04-04", "Raj Enterprises", "MS turning scrap", "9620", "0.371", "3569.02", "PB65BF4663", "Advance 100%", ""];
  const csv = [TEMPLATE_HEADERS.join(","), example.join(",")].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "scrap-sales-template.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}

const STATUS_BADGE = {
  new:    { cls: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300", label: "New" },
  update: { cls: "bg-blue-500/12 text-blue-700 dark:text-blue-300", label: "Update" },
  error:  { cls: "bg-red-500/12 text-red-700 dark:text-red-300", label: "Error" },
};

// ── Import modal (pick → preview → commit) ──
function ScrapImportModal({ onClose, onDone }) {
  const [rows, setRows] = useState(null);        // parsed backend rows
  const [fileName, setFileName] = useState("");
  const [mode, setMode] = useState("insert");    // insert | upsert
  const [preview, setPreview] = useState(null);  // { summary, rows }
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const runPreview = useCallback(async (parsedRows, nextMode) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/sales/scrap/bulk/preview`, { rows: parsedRows, mode: nextMode });
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that file");
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }, []);

  const ingest = useCallback((text, name) => {
    const { headers, rows: raw } = parseCSVText(text);
    const keys = headers.map(canonKey);
    if (!keys.includes("customer") || !keys.includes("qty_kg") || !(keys.includes("rate_usd") || keys.includes("value_usd"))) {
      toast.error("CSV needs Customer, Qty and a Rate (USD) or Total Value column");
      return;
    }
    const parsed = raw.map((r) => csvRowToSale(keys, r)).filter((s) => s.customer || s.material || s.qty_kg);
    if (!parsed.length) { toast.error("No data rows found in that file"); return; }
    setRows(parsed);
    setFileName(name || "pasted data");
    runPreview(parsed, mode);
  }, [mode, runPreview]);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => ingest(e.target.result, file.name);
    reader.readAsText(file);
  };

  const toggleMode = () => {
    const next = mode === "insert" ? "upsert" : "insert";
    setMode(next);
    if (rows) runPreview(rows, next);
  };

  const importable = preview ? (preview.summary.new || 0) + (preview.summary.update || 0) : 0;

  const doImport = async () => {
    if (!importable || importing) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/sales/scrap/bulk`, { rows, mode });
      const { count = 0, updated = 0 } = res.data || {};
      const bits = [];
      if (count) bits.push(`${count} added`);
      if (updated) bits.push(`${updated} updated`);
      toast.success(`Import complete — ${bits.join(", ") || "no changes"}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="scrap-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" style={{ color: ACCENT }} /> Import scrap sales
          </DialogTitle>
          <DialogDescription>
            Upload your sheet as CSV. The Total Value (USD) is taken as-is; the ₹ reference is
            worked out at the current FX. Nothing is written until you confirm the preview.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50 transition-colors">
              <Upload className="w-4 h-4" /> {rows ? "Choose another file" : "Choose .csv file"}
            </button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
            <button onClick={downloadTemplate} title="Download the column template"
                    className="inline-flex items-center gap-1.5 text-[12.5px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] px-2 py-1.5 rounded-lg transition-colors">
              <Download className="w-4 h-4" /> Template
            </button>
          </div>
          <label className="inline-flex items-center gap-2 text-[12.5px] cursor-pointer select-none" title="Overwrite sales whose invoice number already exists">
            <button type="button" role="switch" aria-checked={mode === "upsert"} onClick={toggleMode}
                    className={`relative w-9 h-5 rounded-full transition-colors ${mode === "upsert" ? "bg-violet-600" : "bg-[hsl(var(--muted-foreground))]/30"}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${mode === "upsert" ? "translate-x-4" : ""}`} />
            </button>
            <span className={mode === "upsert" ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))]"}>Update existing invoices</span>
          </label>
        </div>

        {!rows && (
          <div onClick={() => fileRef.current?.click()}
               className="cursor-pointer rounded-lg border-2 border-dashed border-[hsl(var(--border))] hover:border-violet-500/50 px-4 py-8 text-center transition-colors">
            <Upload className="w-6 h-6 mx-auto text-[hsl(var(--muted-foreground))]/60 mb-2" />
            <div className="text-[13px] text-[hsl(var(--muted-foreground))]">Drop a CSV here or click to choose</div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))]/70 mt-1.5">
              Reads both this page's export and your existing scrap-sale sheet. Dates like
              <span className="font-mono-data"> 04.April.2026</span> are understood.
            </div>
          </div>
        )}

        {busy && (
          <div className="flex items-center justify-center py-6 text-[hsl(var(--muted-foreground))] text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Reading {fileName}…
          </div>
        )}

        {preview && !busy && (
          <div className="space-y-2">
            <div className="flex items-center gap-4 text-[12.5px] px-1">
              <span className="inline-flex items-center gap-1.5 text-emerald-600"><CheckCircle2 className="w-4 h-4" /> {preview.summary.new || 0} new</span>
              <span className="inline-flex items-center gap-1.5 text-blue-600"><RefreshCw className="w-4 h-4" /> {preview.summary.update || 0} update{(preview.summary.update || 0) === 1 ? "" : "s"}</span>
              {(preview.summary.error || 0) > 0 && (
                <span className="inline-flex items-center gap-1.5 text-red-600"><AlertTriangle className="w-4 h-4" /> {preview.summary.error} error{preview.summary.error === 1 ? "" : "s"}</span>
              )}
              <span className="ml-auto text-[hsl(var(--muted-foreground))]">{fileName}</span>
            </div>
            <div className="max-h-[38vh] overflow-y-auto rounded-lg border border-[hsl(var(--border))]">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-[hsl(var(--card))]">
                  <tr className="border-b border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">
                    <th className="text-left font-medium px-2.5 py-1.5">#</th>
                    <th className="text-left font-medium px-2.5 py-1.5">Customer</th>
                    <th className="text-left font-medium px-2.5 py-1.5">Material</th>
                    <th className="text-right font-medium px-2.5 py-1.5">Value $</th>
                    <th className="text-left font-medium px-2.5 py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((v) => {
                    const b = STATUS_BADGE[v.status] || STATUS_BADGE.error;
                    return (
                      <tr key={v.index} className="border-b border-[hsl(var(--border))]/40 last:border-0">
                        <td className="px-2.5 py-1.5 text-[hsl(var(--muted-foreground))] font-mono-data">{v.index + 1}</td>
                        <td className="px-2.5 py-1.5 max-w-[150px] truncate" title={v.customer}>{v.customer || "—"}</td>
                        <td className="px-2.5 py-1.5 text-[hsl(var(--muted-foreground))] max-w-[130px] truncate" title={v.material}>{v.material || "—"}</td>
                        <td className="px-2.5 py-1.5 text-right font-mono-data">{v.value_usd ? fmtUSD(v.value_usd) : "—"}</td>
                        <td className="px-2.5 py-1.5">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${b.cls}`}>{b.label}</span>
                          {v.reason && <span className="ml-1.5 text-[11px] text-red-600">{v.reason}</span>}
                          {v.status === "update" && v.changes?.length > 0 && (
                            <span className="ml-1.5 text-[11px] text-[hsl(var(--muted-foreground))]">changes: {v.changes.join(", ")}</span>
                          )}
                          {v.status === "update" && v.changes && v.changes.length === 0 && (
                            <span className="ml-1.5 text-[11px] text-[hsl(var(--muted-foreground))]">no changes</span>
                          )}
                          {v.warning && <span className="ml-1.5 text-[11px] text-amber-600">{v.warning}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose}
                  className="px-4 py-2 rounded-md text-sm border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/50 transition-colors">
            Cancel
          </button>
          <button onClick={doImport} disabled={!importable || importing} data-testid="scrap-import-commit"
                  title={importable ? "" : "Nothing to import — fix the errors or choose a file"}
                  className={`px-4 py-2 rounded-md text-sm font-semibold text-white transition-colors inline-flex items-center gap-1.5 ${importable && !importing ? "bg-violet-600 hover:bg-violet-500 rl-lit-btn" : "bg-violet-600/40 cursor-not-allowed"}`}>
            {importing && <Loader2 className="w-4 h-4 animate-spin" />}
            Import {importable || 0} row{importable === 1 ? "" : "s"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ScrapSalesPage() {
  const { user } = useContext(AuthContext);
  const { density } = useTableDensity();
  const isCompact = density === "compact";

  const perms = user?.permissions || {};
  const isAdmin = user?.role === "Admin";
  const canManage = isAdmin || !!perms.can_manage_scrap_sales;
  const canExport = isAdmin || !!perms.can_export;
  const canImport = canManage && (isAdmin || !!perms.can_import);

  const [sales, setSales] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);      // { sale: null | sale }
  const [importOpen, setImportOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [salesRes, metaRes] = await Promise.all([
        axios.get(`${API}/sales/scrap`),
        axios.get(`${API}/sales/scrap/meta`).catch(() => ({ data: null })),
      ]);
      setSales(Array.isArray(salesRes.data) ? salesRes.data : []);
      setMeta(metaRes.data);
    } catch (e) {
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    if (!q.trim()) return sales;
    const needle = q.trim().toLowerCase();
    return sales.filter((s) =>
      [s.customer, s.material, s.invoice_no, s.vehicle_no].some((v) => (v || "").toLowerCase().includes(needle))
    );
  }, [sales, q]);

  const fy = useMemo(() => fyBounds(), []);
  const fyTotals = useMemo(() => {
    const rows = sales.filter((s) => s.invoice_date >= fy.from && s.invoice_date <= fy.to);
    return {
      count: rows.length,
      kg: rows.reduce((a, s) => a + (s.qty_kg || 0), 0),
      usd: rows.reduce((a, s) => a + (s.value_usd || 0), 0),
      inr: rows.reduce((a, s) => a + (s.value_inr || 0), 0),
    };
  }, [sales, fy]);

  // Month groups (desc), each with subtotals — the account-book read.
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((s) => {
      const k = monthKey(s.invoice_date);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    });
    return [...map.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([k, rows]) => ({
        key: k,
        label: monthLabel(k),
        rows: rows.sort((a, b) => (b.invoice_date || "").localeCompare(a.invoice_date || "") || (b.invoice_no || "").localeCompare(a.invoice_no || "")),
        kg: rows.reduce((a, s) => a + (s.qty_kg || 0), 0),
        usd: rows.reduce((a, s) => a + (s.value_usd || 0), 0),
      }));
  }, [filtered]);

  const doDelete = async () => {
    if (!confirmDel || deleting) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/sales/scrap/${confirmDel.id}`);
      toast.success(`Deleted ${confirmDel.invoice_no}`);
      setConfirmDel(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const cellPad = isCompact ? "px-2.5 py-1.5" : "px-3 py-2.5";
  const thCls = `${isCompact ? "px-2.5 py-1.5" : "px-3 py-2"} text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[hsl(var(--muted-foreground))]`;

  return (
    <div data-testid="scrap-sales-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <PageTitle color={ACCENT} title="Scrap Sales" count={loading ? "—" : fyTotals.count} unit={`sales in ${fy.label}`} />
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]/60" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, invoice, vehicle…"
              data-testid="scrap-search"
              className="h-8 pl-8 pr-8 text-xs rounded-full rl-search bg-[hsl(var(--muted))]/50 border border-transparent focus:bg-[hsl(var(--card))] focus:border-violet-500/50 text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus:outline-none w-full sm:w-64"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" aria-label="Clear search">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {canExport && sales.length > 0 && (
            <button onClick={() => exportScrapCSV(filtered)} data-testid="scrap-export"
                    title="Export the current view to CSV"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50 transition-colors">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
          )}
          {canImport && (
            <button onClick={() => setImportOpen(true)} data-testid="scrap-import"
                    title="Import scrap sales from a CSV"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50 transition-colors">
              <Upload className="w-3.5 h-3.5" /> Import
            </button>
          )}
          {canManage && (
            <button onClick={() => setModal({ sale: null })} data-testid="scrap-add"
                    className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full rl-lit-btn bg-violet-600 hover:bg-violet-500 text-white text-[11px] font-semibold uppercase tracking-[0.05em] transition-colors">
              <Plus className="w-3.5 h-3.5" /> Record sale
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 rounded-xl bg-[hsl(var(--muted))]/35 border border-[hsl(var(--border))]/50 px-4 py-2.5 mb-4 text-[12.5px]" data-testid="scrap-fy-strip">
        <span className="text-[hsl(var(--muted-foreground))] font-medium">{fy.label}</span>
        <span><span className="text-[hsl(var(--muted-foreground))]">Weight </span><span className="font-mono-data font-semibold">{fmtKg(fyTotals.kg)} kg</span></span>
        <span><span className="text-[hsl(var(--muted-foreground))]">Value </span><span className="font-mono-data font-semibold">{fmtUSD(fyTotals.usd)}</span></span>
        <span><span className="text-[hsl(var(--muted-foreground))]">≈ </span><span className="font-mono-data font-semibold">{fmtINR(fyTotals.inr)}</span></span>
        {meta?.fx_inr_usd ? (
          <span className="text-[11px] text-[hsl(var(--muted-foreground))]/80 ml-auto" title="₹ is a reference only, at the admin FX rate (shared with the Stock Center)">
            @ ₹{(1 / meta.fx_inr_usd).toFixed(2)}/USD
          </span>
        ) : null}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-[hsl(var(--muted-foreground))]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading sales…
        </div>
      ) : sales.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[hsl(var(--border))] px-6 py-12 text-center" data-testid="scrap-empty">
          <Recycle className="w-8 h-8 mx-auto mb-3 text-[hsl(var(--muted-foreground))]/50" />
          <p className="text-sm font-medium mb-1">No scrap sales recorded yet</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] max-w-sm mx-auto mb-4">
            Every truck of turnings and offcuts sold to a dealer gets one entry here — enter the
            weight and USD rate, and the sale value is worked out for you.
          </p>
          {canManage && (
            <button onClick={() => setModal({ sale: null })}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full rl-lit-btn bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold transition-colors">
              <Plus className="w-4 h-4" /> Record your first sale
            </button>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[hsl(var(--border))] px-6 py-10 text-center text-sm text-[hsl(var(--muted-foreground))]">
          No sales match “{q}”. <button className="underline hover:text-[hsl(var(--foreground))]" onClick={() => setQ("")}>Clear search</button>
        </div>
      ) : (
        <div className="rounded-xl border border-[hsl(var(--border))] overflow-x-auto bg-[hsl(var(--card))]">
          <table className="w-full text-[12.5px] min-w-[760px]" data-testid="scrap-table">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                <th className={`${thCls} text-left`}>Invoice</th>
                <th className={`${thCls} text-left`}>Date</th>
                <th className={`${thCls} text-left`}>Customer</th>
                <th className={`${thCls} text-left`}>Material</th>
                <th className={`${thCls} text-right`}>Kg</th>
                <th className={`${thCls} text-right`}>$/kg</th>
                <th className={`${thCls} text-right`}>Value $</th>
                <th className={`${thCls} text-right`}>≈ ₹</th>
                {canManage && <th className={`${thCls} text-right w-[76px]`} aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <React.Fragment key={g.key}>
                  <tr className="bg-[hsl(var(--muted))]/45">
                    <td colSpan={canManage ? 9 : 8} className={`${isCompact ? "px-2.5 py-1" : "px-3 py-1.5"}`}>
                      <div className="flex items-baseline justify-between">
                        <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">{g.label}</span>
                        <span className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))]">
                          {fmtKg(g.kg)} kg · {fmtUSD(g.usd)}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {g.rows.map((s) => (
                    <tr key={s.id} className="border-b border-[hsl(var(--border))]/50 last:border-0 hover:bg-[hsl(var(--muted))]/25 group transition-colors" data-testid={`scrap-row-${s.invoice_no}`}>
                      <td className={`${cellPad} font-mono-data text-xs whitespace-nowrap`} title={s.invoice_no}>
                        {(s.invoice_no || "").split("/").pop()}
                      </td>
                      <td className={`${cellPad} text-[hsl(var(--muted-foreground))] whitespace-nowrap`}>{fmtDate(s.invoice_date)}</td>
                      <td className={`${cellPad} font-medium max-w-[220px] truncate`} title={s.customer}>{s.customer}</td>
                      <td className={`${cellPad} text-[hsl(var(--muted-foreground))] max-w-[180px] truncate`} title={s.material}>{s.material}</td>
                      <td className={`${cellPad} text-right font-mono-data`}>{fmtKg(s.qty_kg)}</td>
                      <td className={`${cellPad} text-right font-mono-data`}>{fmtRate(s.rate_usd)}</td>
                      <td className={`${cellPad} text-right font-mono-data font-semibold`} title={`${fmtKg(s.qty_kg)} kg × ${fmtRate(s.rate_usd)}${s.vehicle_no ? ` · Vehicle ${s.vehicle_no}` : ""}`}>
                        {fmtUSD(s.value_usd)}
                      </td>
                      <td className={`${cellPad} text-right font-mono-data text-[hsl(var(--muted-foreground))]`}>{fmtINR(s.value_inr)}</td>
                      {canManage && (
                        <td className={`${cellPad} text-right whitespace-nowrap`}>
                          <span className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" style={{ pointerEvents: "auto" }}>
                            <button onClick={() => setModal({ sale: s })} title={`Edit ${s.invoice_no}`} aria-label={`Edit ${s.invoice_no}`}
                                    className="p-1.5 rounded-md text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/60 transition-colors">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setConfirmDel(s)} title={`Delete ${s.invoice_no}`} aria-label={`Delete ${s.invoice_no}`}
                                    className="p-1.5 rounded-md text-[hsl(var(--muted-foreground))] hover:text-red-500 hover:bg-red-500/10 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ScrapSaleModal
          sale={modal.sale} meta={meta}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); fetchAll(); }}
        />
      )}

      {importOpen && (
        <ScrapImportModal
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); fetchAll(); }}
        />
      )}

      {confirmDel && (
        <Dialog open onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" /> Delete this sale?
              </DialogTitle>
              <DialogDescription>
                {confirmDel.invoice_no} — {confirmDel.customer}, {fmtKg(confirmDel.qty_kg)} kg,{" "}
                {fmtUSD(confirmDel.value_usd)}. This removes the ledger entry permanently.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)}
                      className="px-4 py-2 rounded-md text-sm border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/50 transition-colors">
                Cancel
              </button>
              <button onClick={doDelete} disabled={deleting}
                      className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-red-600 hover:bg-red-500 transition-colors inline-flex items-center gap-1.5">
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />} Delete
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
